import { useRef, useEffect } from 'react';

// Gently auto-scrolls a horizontally-overflowing container, and lets the user
// drive it three ways: mouse wheel, click-and-drag, or touch swipe. Auto-scroll
// pauses while the user hovers / drags / touches it, and loops back to the start
// on reaching the end. Returns a ref to attach to the scrolling element.
export default function useAutoScroll({ speed = 1, intervalMs = 20, deps = [] } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Keep scrolling instant so the seamless half-width jump never animates.
    el.style.scrollBehavior = 'auto';

    let paused = false;   // hover / touch
    let isDown = false;    // mouse drag in progress
    let dragged = false;   // moved enough to count as a drag (suppress click)
    let startX = 0;
    let startScroll = 0;

    const pause = () => { paused = true; };
    const resume = () => { paused = false; };

    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);
    el.addEventListener('touchstart', pause, { passive: true });
    el.addEventListener('touchend', resume, { passive: true });

    // --- Click-and-drag to scroll (desktop) ---
    const onMouseDown = (e) => {
      isDown = true;
      dragged = false;
      startX = e.pageX;
      startScroll = el.scrollLeft;
      el.style.cursor = 'grabbing';
    };
    const onMouseMove = (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (Math.abs(dx) > 4) dragged = true;
      el.scrollLeft = startScroll - dx;
    };
    const onMouseUp = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = 'grab';
    };
    // Swallow the click that fires right after a drag so it doesn't navigate.
    const onClickCapture = (e) => {
      if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; }
    };

    el.style.cursor = 'grab';
    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    el.addEventListener('click', onClickCapture, true);

    // --- Mouse wheel -> horizontal scroll ---
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (!delta) return;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;
      e.preventDefault();
      el.scrollLeft += delta;
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    // --- Auto-scroll loop (seamless / infinite) ---
    // The caller renders the list twice. When we pass the end of the first
    // copy (half the total width) we instantly subtract half — because the
    // second copy is identical, the jump is invisible and scrolling looks
    // continuous, never snapping back to the start.
    const id = setInterval(() => {
      if (paused || isDown) return;
      if (el.scrollWidth <= el.clientWidth) return; // nothing to scroll
      el.scrollLeft += speed;
      if (el.scrollLeft >= el.scrollWidth / 2) {
        el.scrollLeft -= el.scrollWidth / 2;
      }
    }, intervalMs);

    return () => {
      clearInterval(id);
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchend', resume);
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('click', onClickCapture, true);
      el.removeEventListener('wheel', onWheel);
    };
  }, deps);

  return ref;
}